import React, { useState, useEffect } from "react";
import axios from "axios";

const Settings = () => {
    const [deliveryAvailable, setDeliveryAvailable] = useState(false);
    const [ignoreDeliveryState, setIgnoreDeliveryState] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [printerOnlineLatest, setPrinterOnlineLatest] = useState(false);
    const [printerOnlineTime, setPrinterOnlineTime] = useState(null);
    const [currentTime, setCurrentTime] = useState(new Date().toLocaleString());

    // Fetch JWT from localStorage
    const getAuthToken = () => localStorage.getItem("token");

    // Axios instance with Authorization header
    const axiosInstance = axios.create({
        baseURL: "/api",
        headers: {
            Authorization: `Bearer ${getAuthToken()}`,
        },
    });

    // Fetch settings from the server
    const fetchSetting = async () => {
        setLoading(true);
        try {
            const [deliveryResponse, printerResponse, ignoreDeliveryState] =
                await Promise.all([
                    axiosInstance.get("/admin/settings/deliveryAvailable"),
                    axiosInstance.get("/admin/settings/latestPrinterOnline"),
                    axiosInstance.get("/admin/settings/ignoreDeliveryState"),
                ]);

            setDeliveryAvailable(deliveryResponse.data?.value || false);
            setIgnoreDeliveryState(ignoreDeliveryState.data?.value || false);
            if (printerResponse.data) {
                setPrinterOnlineLatest(printerResponse.data.value);
                setPrinterOnlineTime(printerResponse.data.time);
            }
        } catch (err) {
            setError("Error fetching the settings");
        } finally {
            setLoading(false);
        }
    };

    // Save delivery setting to the server
    const saveSetting = async () => {
        setLoading(true);
        try {
            await axiosInstance.post("/admin/settings", {
                key: "deliveryAvailable",
                value: deliveryAvailable,
            });
            await axiosInstance.post("/admin/settings", {
                key: "ignoreDeliveryState",
                value: ignoreDeliveryState,
            });
            setSuccess("Setting updated successfully");
        } catch (err) {
            setError("Error updating the setting");
        } finally {
            setLoading(false);
        }
    };

    // Delete delivery setting from the server
    const deleteSetting = async () => {
        setLoading(true);
        try {
            await axiosInstance.delete("/admin/settings/deliveryAvailable");
            setDeliveryAvailable(false);
            setSuccess("Setting deleted successfully");
        } catch (err) {
            setError("Error deleting the setting");
        } finally {
            setLoading(false);
        }
    };

    // Periodically fetch printer online status
    useEffect(() => {
        const intervalId = setInterval(() => {
            axiosInstance
                .get("/admin/settings/latestPrinterOnline")
                .then((response) => {
                    if (response.data) {
                        setPrinterOnlineLatest(response.data.value);
                        setPrinterOnlineTime(response.data.time);
                    }
                })
                .catch(() => {
                    setError("Error fetching printer status");
                });

            setCurrentTime(new Date().toLocaleString()); // Update displayed current time
        }, 1000); // Fetch every 5 seconds

        return () => clearInterval(intervalId); // Cleanup interval on unmount
    }, []);

    // Fetch initial settings on component mount
    useEffect(() => {
        fetchSetting();
    }, []);

    return (
        <div className="max-w-md mx-auto bg-white p-6 rounded-md shadow-md">
            <div>
                <p className="text-sm text-gray-500 mb-4">
                    Current Time: {currentTime}
                </p>
                <p className="text-sm text-gray-500 mb-4">
                    Latest Printer Online:{" "}
                    {new Date(printerOnlineLatest).toLocaleString()}
                </p>
                {printerOnlineTime && (
                    <p className="text-sm text-gray-500 mb-4">
                        Last Online Time:{" "}
                        {new Date(printerOnlineTime).toLocaleString()}
                    </p>
                )}
            </div>
            <h1 className="text-xl font-bold mb-4">Manage Delivery Setting</h1>

            {loading && <p>Loading...</p>}
            {error && <p className="text-red-500">{error}</p>}
            {success && <p className="text-green-500">{success}</p>}

            <div className="flex items-center space-x-4 mb-4">
                <label
                    htmlFor="deliveryAvailable"
                    className="text-sm font-medium"
                >
                    Delivery Available:
                </label>
                <input
                    type="checkbox"
                    id="deliveryAvailable"
                    checked={deliveryAvailable}
                    onChange={(e) => setDeliveryAvailable(e.target.checked)}
                    className="h-5 w-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                />
            </div>

            <div className="flex items-center space-x-4 mb-4">
                <label
                    htmlFor="ignoreDeliveryState"
                    className="text-sm font-medium"
                >
                    Ignore Delivery State:
                </label>
                <input
                    type="checkbox"
                    id="ignoreDeliveryState"
                    checked={ignoreDeliveryState}
                    onChange={(e) => setIgnoreDeliveryState(e.target.checked)}
                    className="h-5 w-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                />
            </div>

            <div className="flex space-x-4">
                <button
                    onClick={saveSetting}
                    className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-700"
                >
                    Save
                </button>
                <button
                    onClick={deleteSetting}
                    className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-700"
                >
                    Delete
                </button>
            </div>
        </div>
    );
};

export default Settings;
